/**
 * narrative/dialog.ts — NPC Dialog Engine
 *
 * Powers structured NPC conversations with branching options, quest integration,
 * and free-text input that routes through the Cortex for narrative responses.
 *
 * Dialog flow:
 *   1. Player clicks NPC → openDialog() builds initial dialog page
 *   2. Player selects an option → handleDialogOption() processes the choice
 *   3. Options can: continue dialog, accept/complete quests, open shop, start duel
 *   4. Free text input passes through Cortex for narrative response
 *   5. Dialog closes via explicit close option or Escape key
 *
 * Quest giver marks are computed by computeQuestMark() — called each frame
 * to determine what icon floats above an NPC's head.
 */

import type { GameState, DialogOption, NpcState, QuestGiverMark, QuestProgress } from "../engine/state.js";
import { QUEST_INDEX } from "./quests.js";
import type { Quest } from "./quests.js";
import { addToast } from "../render/toasts.js";

// ─── Quest Giver Mark Computation ──────────────────────────────────────────

/**
 * Compute what quest marker should float above this NPC's head.
 * Called each frame for all visible NPCs.
 */
export function computeQuestMark(
  npc: NpcState,
  playerQuests: QuestProgress[],
  completedQuestIds: Set<string>,
  playerLevel: number,
): QuestGiverMark {
  // Only quest givers, instructors, arena masters get quest marks
  if (npc.role !== "quest_giver" && npc.role !== "instructor" && npc.role !== "arena_master" && npc.role !== "dummy_master") {
    return null;
  }

  const npcQuestIds = npc.questIds ?? getNpcDefaultQuests(npc);

  // Check for ready-to-complete quests first (gold ?)
  for (const qid of npcQuestIds) {
    const progress = playerQuests.find(q => q.questId === qid);
    if (progress && progress.status === "active") {
      const quest = QUEST_INDEX.get(qid);
      if (quest && progress.completedSteps.length >= quest.steps.length) {
        return "ready_complete";
      }
    }
  }

  // Check for in-progress quests (silver ?)
  for (const qid of npcQuestIds) {
    const progress = playerQuests.find(q => q.questId === qid);
    if (progress && progress.status === "active") {
      return "in_progress";
    }
  }

  // Check for available quests (gold !)
  for (const qid of npcQuestIds) {
    if (completedQuestIds.has(qid)) continue;
    const quest = QUEST_INDEX.get(qid);
    if (!quest) continue;
    // Check prerequisites
    if (quest.prerequisites?.some(p => !completedQuestIds.has(p))) continue;
    // Check level requirement (use reputation as effective level)
    if (quest.rarity === "legendary" && playerLevel < 10) continue;
    return "available";
  }

  // Has quests but player doesn't qualify
  const hasAnyQuest = npcQuestIds.some(qid => QUEST_INDEX.has(qid) && !completedQuestIds.has(qid));
  if (hasAnyQuest) return "unavailable";

  return null;
}

/**
 * Default quest mapping for NPCs based on zone + role.
 * Used when npc.questIds is not explicitly set.
 */
function getNpcDefaultQuests(npc: NpcState): string[] {
  switch (npc.id) {
    case "npc_commander_vex":  return ["q_know_your_stance"];
    case "npc_renna":          return ["q_ring_ready"];
    case "npc_hauk":           return ["q_first_strike", "q_sparring_hauk"];
    case "npc_old_barnaby":    return ["q_dummy_duty"];
    default:                   return [];
  }
}

// ─── Dialog Builder ──────────────────────────────────────────────────────────

/**
 * Open a dialog with an NPC. Builds the initial dialog state based on
 * the NPC's role, available quests, and player's current quest progress.
 */
export function openDialog(state: GameState, npc: NpcState): void {
  const d = state.dialog;
  d.open = true;
  d.npcId = npc.id;
  d.npcName = npc.name;
  d.npcTitle = npc.title;
  d.page = 0;
  d.hoveredOption = -1;
  d.freeInputValue = "";
  d.openedAt = performance.now() / 1000;

  // Auto-complete "talk to" quest steps when dialog opens with this NPC
  completeTalkSteps(state, npc);

  // Build dialog text and options based on NPC state
  buildDialogPage(state, npc);
}

/**
 * Auto-complete quest steps that require talking to this specific NPC.
 * Called when openDialog is triggered.
 */
function completeTalkSteps(state: GameState, npc: NpcState): void {
  for (const progress of state.player.quests) {
    if (progress.status !== "active") continue;
    const quest = QUEST_INDEX.get(progress.questId);
    if (!quest) continue;

    for (const step of quest.steps) {
      if (progress.completedSteps.includes(step.id)) continue;

      // Check previous steps are done
      const stepIdx = quest.steps.indexOf(step);
      if (stepIdx > 0 && !progress.completedSteps.includes(quest.steps[stepIdx - 1].id)) break;

      // Match "Talk to X" / "Return to X" steps by checking NPC name in description
      const desc = step.description.toLowerCase();
      const npcName = npc.name.toLowerCase();
      if ((desc.includes("talk to") || desc.includes("return to") || desc.includes("speak with")) && desc.includes(npcName)) {
        progress.completedSteps.push(step.id);
        progress.progress = progress.completedSteps.length / quest.steps.length;
        addToast(state, `Quest updated: ${step.description}`, "info", 2);
      }
    }
  }
}

/**
 * Build the dialog page content — text + options.
 * This is the core dialog logic that examines quest state and NPC role.
 */
function buildDialogPage(state: GameState, npc: NpcState): void {
  const d = state.dialog;
  const options: DialogOption[] = [];
  const npcQuestIds = npc.questIds ?? getNpcDefaultQuests(npc);

  // Greeting text — use current dialogue line
  const greetLine = npc.dialogue[npc.dialogueIndex % npc.dialogue.length];
  let text = greetLine;

  // Check for quest-related options
  const completedIds = new Set(
    state.player.quests.filter(q => q.status === "complete").map(q => q.questId)
  );

  // Ready to complete quests — highest priority
  for (const qid of npcQuestIds) {
    const progress = state.player.quests.find(q => q.questId === qid);
    if (!progress || progress.status !== "active") continue;
    const quest = QUEST_INDEX.get(qid);
    if (!quest) continue;
    if (progress.completedSteps.length >= quest.steps.length) {
      text = `You've done it! ${quest.name} — completed. I knew you had it in you. Come, let me see...`;
      options.push({
        id: `complete_${qid}`,
        text: `Complete: ${quest.name}`,
        action: "complete_quest",
        questId: qid,
      });
    }
  }

  // Available quests
  for (const qid of npcQuestIds) {
    if (completedIds.has(qid)) continue;
    const existingProgress = state.player.quests.find(q => q.questId === qid);
    if (existingProgress && existingProgress.status === "active") continue; // already active
    const quest = QUEST_INDEX.get(qid);
    if (!quest) continue;
    if (quest.prerequisites?.some(p => !completedIds.has(p))) {
      options.push({
        id: `quest_locked_${qid}`,
        text: `${quest.name} (locked)`,
        action: "continue",
        disabled: true,
        disabledReason: "Complete prerequisite quests first.",
      });
      continue;
    }
    options.push({
      id: `accept_${qid}`,
      text: `Quest: ${quest.name}`,
      action: "accept_quest",
      questId: qid,
    });
  }

  // In-progress quest updates
  for (const qid of npcQuestIds) {
    const progress = state.player.quests.find(q => q.questId === qid);
    if (!progress || progress.status !== "active") continue;
    const quest = QUEST_INDEX.get(qid);
    if (!quest) continue;
    if (progress.completedSteps.length < quest.steps.length) {
      const nextStep = quest.steps.find(s => !progress.completedSteps.includes(s.id));
      if (nextStep) {
        options.push({
          id: `progress_${qid}`,
          text: `Ask about: ${quest.name}`,
          action: "continue",
        });
      }
    }
  }

  // Role-specific options
  switch (npc.role) {
    case "merchant":
      options.push({ id: "shop", text: "Show me your wares.", action: "shop" });
      break;
    case "instructor":
      options.push({ id: "train", text: "Teach me something.", action: "train" });
      break;
    case "arena_master":
      options.push({ id: "duel", text: "I want to duel.", action: "duel" });
      break;
  }

  // Always add chat option
  d.showFreeInput = true;

  // Always add goodbye
  options.push({ id: "close", text: "Goodbye.", action: "close" });

  d.text = text;
  d.options = options;
}

// ─── Dialog Actions ────────────────────────────────────────────────────────

/**
 * Handle a player selecting a dialog option.
 * Returns true if the dialog should remain open, false if it should close.
 */
export function handleDialogOption(state: GameState, optionId: string): boolean {
  const d = state.dialog;
  const option = d.options.find(o => o.id === optionId);
  if (!option || option.disabled) return true;

  switch (option.action) {
    case "close":
      closeDialog(state);
      return false;

    case "accept_quest": {
      if (!option.questId) return true;
      const quest = QUEST_INDEX.get(option.questId);
      if (!quest) return true;

      // Add quest to player's active quests
      const existing = state.player.quests.find(q => q.questId === option.questId);
      if (existing) {
        existing.status = "active";
        existing.startedAt = Date.now();
      } else {
        state.player.quests.push({
          questId: option.questId!,
          status: "active",
          completedSteps: [],
          progress: 0,
          startedAt: Date.now(),
          completedAt: 0,
        });
      }

      // Add to tracker
      if (!state.questUI.trackedQuestIds.includes(option.questId)) {
        state.questUI.trackedQuestIds.push(option.questId);
      }

      // Show quest accept overlay
      state.questUI.overlayQuestId = option.questId;
      state.questUI.overlayAction = "accept";
      state.questUI.overlayTime = performance.now() / 1000;

      // Update dialog to show quest narrative
      d.text = quest.narrative;
      d.options = [
        { id: "quest_accepted", text: "I'll get it done.", action: "close" },
        { id: "quest_details", text: "Tell me more about the steps.", action: "continue" },
      ];
      return true;
    }

    case "complete_quest": {
      if (!option.questId) return true;
      const quest = QUEST_INDEX.get(option.questId);
      if (!quest) return true;
      const progress = state.player.quests.find(q => q.questId === option.questId);
      if (!progress) return true;

      // Mark complete
      progress.status = "complete";
      progress.completedAt = Date.now();
      progress.progress = 1;

      // Grant rewards
      const reward = quest.reward;
      if (reward.currency) state.player.currency += reward.currency;
      if (reward.reputation) state.player.reputation += reward.reputation;

      // Remove from tracker
      state.questUI.trackedQuestIds = state.questUI.trackedQuestIds.filter(
        id => id !== option.questId
      );

      // Show completion overlay
      state.questUI.overlayQuestId = option.questId;
      state.questUI.overlayAction = "complete";
      state.questUI.overlayTime = performance.now() / 1000;

      // Update dialog with reward info
      const rewardParts: string[] = [];
      if (reward.currency) rewardParts.push(`${reward.currency} gold`);
      if (reward.reputation) rewardParts.push(`${reward.reputation} reputation`);
      if (reward.title) rewardParts.push(`Title: "${reward.title}"`);

      d.text = `Quest complete! Well done.\n\nRewards: ${rewardParts.join(", ") || "the satisfaction of a job done."}`;
      d.options = [{ id: "close", text: "Thank you.", action: "close" }];
      return true;
    }

    case "train": {
      d.text = "Training is a journey, not a destination. Here's what I can teach you:\n\n" +
        "• **Stance** — The foundation of every strike. Stand firm, weight centered.\n" +
        "• **Timing** — Watch your opponent. Every attack has a tell.\n" +
        "• **Combos** — Chain your strikes. 1-2-3, quick and clean.\n\n" +
        "Practice on the dummies. Come back when your muscles remember what your mind forgets.";
      d.options = [
        { id: "train_more", text: "Any other advice?", action: "continue" },
        { id: "close", text: "I'll get to work.", action: "close" },
      ];
      return true;
    }

    case "duel": {
      d.text = "Ready for the ring, are you? Let me explain the rules:\n\n" +
        "• Both fighters start at full health.\n" +
        "• Three-count before the bout begins.\n" +
        "• First to drop the other's HP to zero wins.\n" +
        "• No abilities that affect the arena itself.\n\n" +
        "When you're ready, step into the ring and I'll start the countdown.";
      d.options = [
        { id: "close", text: "I'm ready. Let's go.", action: "close" },
        { id: "close_notyet", text: "Not just yet.", action: "close" },
      ];
      return true;
    }

    case "shop": {
      closeDialog(state);
      // Shop would be opened by the game loop
      return false;
    }

    case "continue":
    default: {
      // Advance dialogue and rebuild
      const npc = state.npcs.find(n => n.id === d.npcId);
      if (npc) {
        npc.dialogueIndex = (npc.dialogueIndex + 1) % npc.dialogue.length;

        // For "ask about quest" options, show quest step hint
        if (optionId.startsWith("progress_")) {
          const qid = optionId.replace("progress_", "");
          const quest = QUEST_INDEX.get(qid);
          const progress = state.player.quests.find(q => q.questId === qid);
          if (quest && progress) {
            const nextStep = quest.steps.find(s => !progress.completedSteps.includes(s.id));
            if (nextStep) {
              d.text = `For "${quest.name}":\n\n${nextStep.description}\n\nHint: ${nextStep.completionHint}`;
              d.options = [{ id: "close", text: "Got it.", action: "close" }];
              return true;
            }
          }
        }

        buildDialogPage(state, npc);
      }
      return true;
    }
  }
}

/**
 * Handle free text input from the player during dialog.
 * This routes through the Cortex for AI-powered narrative responses,
 * or falls back to a simple pattern-match response.
 */
export function handleFreeTextInput(state: GameState, text: string): void {
  const d = state.dialog;
  const npc = state.npcs.find(n => n.id === d.npcId);
  if (!npc) return;

  // Simple keyword-based responses for common questions
  const lower = text.toLowerCase();

  if (lower.includes("quest") || lower.includes("task") || lower.includes("job")) {
    d.text = `${npc.name}: Hmm, let me think about what needs doing around here...`;
    // Rebuild the dialog page to show quest options
    const saved = d.text;
    buildDialogPage(state, npc);
    d.text = saved + "\n\n" + d.text;
  } else if (lower.includes("train") || lower.includes("teach") || lower.includes("learn")) {
    d.text = `${npc.name}: The best training is the kind you do until it stops being training and becomes instinct. Hit those dummies.`;
    d.options = [{ id: "close", text: "Thanks for the advice.", action: "close" }];
  } else if (lower.includes("duel") || lower.includes("fight") || lower.includes("spar")) {
    d.text = `${npc.name}: Looking for a fight? Talk to Renna at the arena ring — she manages all official bouts.`;
    d.options = [{ id: "close", text: "I'll find her.", action: "close" }];
  } else if (lower.includes("who") || lower.includes("name") || lower.includes("about")) {
    d.text = `${npc.name}: I'm ${npc.name}, ${npc.title} here at the Proving Grounds. ${npc.dialogue[0]}`;
    d.options = [
      { id: "continue_chat", text: "Tell me more.", action: "continue" },
      { id: "close", text: "Nice to meet you.", action: "close" },
    ];
  } else if (lower.includes("bye") || lower.includes("goodbye") || lower.includes("leave")) {
    closeDialog(state);
    return;
  } else {
    // General response — the NPC responds in character
    const responses = [
      `${npc.name}: *nods thoughtfully* That's an interesting thought.`,
      `${npc.name}: I appreciate you sharing that. Now, is there something I can help you with?`,
      `${npc.name}: Hmm. I'll think on that. In the meantime — anything else?`,
      `${npc.name}: Words are good, but actions speak louder in this arena.`,
    ];
    d.text = responses[Math.floor(Math.random() * responses.length)];
    d.options = [
      { id: "continue_chat", text: "Actually, I had a question.", action: "continue" },
      { id: "close", text: "That's all for now.", action: "close" },
    ];
  }

  d.freeInputValue = "";
}

/**
 * Close the dialog panel and clean up state.
 */
export function closeDialog(state: GameState): void {
  const d = state.dialog;

  // Mark NPC as no longer in conversation
  if (d.npcId) {
    const npc = state.npcs.find(n => n.id === d.npcId);
    if (npc) {
      npc.inConversation = false;
    }
  }

  d.open = false;
  d.npcId = null;
  d.npcName = null;
  d.npcTitle = null;
  d.text = "";
  d.options = [];
  d.showFreeInput = false;
  d.freeInputValue = "";
  d.page = 0;
  d.hoveredOption = -1;
}

// ─── Quest Auto-Completion Triggers ─────────────────────────────────────────

/**
 * Check game state each frame and auto-complete quest steps that match.
 * This is the "nervous system" that connects world events to quest progress.
 * Call once per frame — early-outs quickly if no active training quests.
 */
export function checkQuestTriggers(state: GameState): void {
  const quests = state.player.quests;
  const zoneId = state.world?.currentZoneId;
  const cs = state.player.combatState;

  for (const progress of quests) {
    if (progress.status !== "active") continue;
    const quest = QUEST_INDEX.get(progress.questId);
    if (!quest) continue;

    for (const step of quest.steps) {
      if (progress.completedSteps.includes(step.id)) continue;

      // Check previous steps are done (sequential completion)
      const stepIdx = quest.steps.indexOf(step);
      if (stepIdx > 0) {
        const prevStep = quest.steps[stepIdx - 1];
        if (!progress.completedSteps.includes(prevStep.id)) break;
      }

      let completed = false;

      // ── Zone entry triggers ──
      if (step.requiresDistrict && zoneId === step.requiresDistrict) {
        completed = true;
      }

      // ── Combat triggers (training dummies) ──
      if (progress.questId === "q_first_strike" && step.id === "s2") {
        // "Strike a training dummy" — any active combat with a dummy
        if (cs.active && cs.targetId) {
          const target = state.creatures.find(c => c.id === cs.targetId);
          if (target && target.speciesId.startsWith("training_dummy")) {
            completed = true;
          }
        }
      }

      if (progress.questId === "q_know_your_stance" && step.id === "s2") {
        // "Land a 3-hit combo" — combo >= 3 on any dummy
        if (cs.active && cs.combo >= 3 && cs.targetId) {
          const target = state.creatures.find(c => c.id === cs.targetId);
          if (target && target.speciesId.startsWith("training_dummy")) {
            completed = true;
          }
        }
      }

      if (progress.questId === "q_ring_ready" && step.id === "s1") {
        // "Defeat the Straw Dummy" — straw dummy has been reset (HP hit 0)
        const strawId = Object.keys(state.trainingDummy.hitsAbsorbed).find(id => {
          const creature = state.creatures.find(c => c.id === id);
          return creature && creature.speciesId === "training_dummy_straw";
        });
        if (strawId && (state.trainingDummy.damageAbsorbed[strawId] ?? 0) > 0) {
          completed = true;
        }
      }

      if (progress.questId === "q_dummy_duty" && step.id === "s2") {
        // "Hit each of the four training dummies" — all 4 have > 0 hits
        const dummies = state.creatures.filter(c => c.speciesId.startsWith("training_dummy"));
        if (dummies.length >= 4) {
          const allHit = dummies.every(d => (state.trainingDummy.hitsAbsorbed[d.id] ?? 0) > 0);
          if (allHit) {
            completed = true;
          }
        }
      }

      // ── NPC talk triggers (completed via dialog system, not auto-trigger) ──
      // Steps like "Talk to Commander Vex" are completed by handleDialogOption
      // when the player actually opens a dialog with the NPC. No auto-trigger here.

      if (completed) {
        progress.completedSteps.push(step.id);
        progress.progress = progress.completedSteps.length / quest.steps.length;

        // Notification
        addToast(state, `Quest updated: ${step.description}`, "info", 2);
      }
    }
  }
}

// ─── Quest Mark Update ──────────────────────────────────────────────────────

/**
 * Update quest marks for all NPCs in the current zone.
 * Call this once per frame (cheap — just a few comparisons per NPC).
 */
export function updateQuestMarks(state: GameState): void {
  const completedIds = new Set(
    state.player.quests.filter(q => q.status === "complete").map(q => q.questId)
  );

  for (const npc of state.npcs) {
    npc.questMark = computeQuestMark(
      npc,
      state.player.quests,
      completedIds,
      state.player.reputation,
    );
  }
}
